import { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Phone, MapPin, Briefcase, Star, User } from 'lucide-react';
import { useContactPersons } from '../../hooks/useContactPersons';
import { useTexts } from '../../hooks/useTexts';
import Breadcrumb from '../../components/common/Breadcrumb';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import { PersonneAContacter } from '../../types/api';

export default function ContactPersonsPage() {
  const { getText } = useTexts();
  const {
    contactPersons,
    clientRelations,
    relations,
    loading,
    error,
    createContactPerson,
    updateContactPerson,
    deleteContactPerson,
    createClientRelation,
    updateClientRelation,
    deleteClientRelation,
    fetchContactPersons,
    fetchClientRelations,
    fetchRelations
  } = useContactPersons();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState<PersonneAContacter | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    nom: '',
    prenoms: '',
    date_naissance: '',
    lieu_naissance: '',
    telephone_domicile: '',
    telephone_bureau: '',
    telephone_portable: '',
    profession: '',
    ville: '',
    type_relation: 0,
    est_principal: false
  });


  useEffect(() => {
    fetchContactPersons();
    fetchClientRelations();
    fetchRelations();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : name === 'type_relation' 
          ? Number(value) 
          : value
    }));
  };


  const handleSubmitPerson = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation : type de relation obligatoire
    if (formData.type_relation === 0) {
      setValidationError('Le type de relation est obligatoire.');
      return;
    }
    
    // Effacer les erreurs précédentes
    setValidationError(null);
    
    try {
      if (editingPerson) {
        // Mettre à jour la personne à contacter
        await updateContactPerson(editingPerson.id, {
          nom: formData.nom,
          prenoms: formData.prenoms,
          date_naissance: formData.date_naissance || undefined,
          lieu_naissance: formData.lieu_naissance || undefined,
          telephone_domicile: formData.telephone_domicile || undefined,
          telephone_bureau: formData.telephone_bureau || undefined,
          telephone_portable: formData.telephone_portable || undefined,
          profession: formData.profession || undefined,
          ville: formData.ville || undefined
        });
        
        // Mettre à jour la relation si elle existe
        const existingRelation = clientRelations.find(rel => rel.personne_a_contacter_id === editingPerson.id);
        if (existingRelation) {
          await updateClientRelation(existingRelation.id, {
            type_relation: formData.type_relation,
            est_principal: formData.est_principal
          });
        } else if (formData.type_relation > 0) {
          // Créer une nouvelle relation si elle n'existe pas
          await createClientRelation({
            personne_a_contacter_id: editingPerson.id,
            type_relation: formData.type_relation,
            est_principal: formData.est_principal
          });
        }
        
        setSuccess('Personne à contacter et relation mises à jour avec succès !');
        
        // Recharger les données
        await fetchContactPersons();
        await fetchClientRelations();
      } else {
        // Créer la personne à contacter
        const newPerson = await createContactPerson({
          nom: formData.nom,
          prenoms: formData.prenoms,
          date_naissance: formData.date_naissance || undefined,
          lieu_naissance: formData.lieu_naissance || undefined,
          telephone_domicile: formData.telephone_domicile || undefined,
          telephone_bureau: formData.telephone_bureau || undefined,
          telephone_portable: formData.telephone_portable || undefined,
          profession: formData.profession || undefined,
          ville: formData.ville || undefined
        });
        
        // Créer la relation (obligatoire)
        await createClientRelation({
          personne_a_contacter_id: newPerson.id,
          type_relation: formData.type_relation,
          est_principal: formData.est_principal
        });
        
        setSuccess('Personne à contacter et relation ajoutées avec succès !');
        
        // Recharger les données
        await fetchContactPersons();
        await fetchClientRelations();
      }
      
      resetForm();
    } catch (err) {
      // L'erreur est gérée par le hook
    }
  };


  const handleEditPerson = (person: PersonneAContacter) => {
    setEditingPerson(person);
    
    // Trouver la relation existante pour cette personne
    const existingRelation = clientRelations.find(rel => rel.personne_a_contacter?.id === person.id || rel.personne_a_contacter_id === person.id);
    
    setFormData({
      nom: person.nom,
      prenoms: person.prenoms,
      date_naissance: person.date_naissance || '',
      lieu_naissance: person.lieu_naissance || '',
      telephone_domicile: person.telephone_domicile || '',
      telephone_bureau: person.telephone_bureau || '',
      telephone_portable: person.telephone_portable || '',
      profession: person.profession || '',
      ville: person.ville || '',
      type_relation: existingRelation?.type_relation_id || existingRelation?.type_relation || 0,
      est_principal: existingRelation?.est_principal || false
    });
    setShowAddForm(true);
  };


  const handleDeletePerson = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette personne à contacter ?')) {
      try {
        await deleteContactPerson(id);
        setSuccess('Personne à contacter supprimée avec succès !');
      } catch (err) {
        // L'erreur est gérée par le hook
      }
    }
  };

  const handleDeleteRelation = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette relation ?')) {
      try {
        await deleteClientRelation(id);
        setSuccess('Relation supprimée avec succès !');
      } catch (err) {
        // L'erreur est gérée par le hook
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenoms: '',
      date_naissance: '',
      lieu_naissance: '',
      telephone_domicile: '',
      telephone_bureau: '',
      telephone_portable: '',
      profession: '',
      ville: '',
      type_relation: 0,
      est_principal: false
    });
    setEditingPerson(null);
    setShowAddForm(false);
    setValidationError(null);
    setSuccess(null);
  };


  if (loading && contactPersons.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('contact_persons.title'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{getText('contact_persons.title')}</h1>
          <p className="text-gray-600 mt-2">
            {getText('contact_persons.description')}
          </p>
        </div>

        {/* Messages d'erreur et de succès */}
        {error && (
          <div className="mb-6">
            <ErrorMessage error={error} onDismiss={() => {}} />
          </div>
        )}
        
        {validationError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800">{validationError}</p>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Liste des personnes à contacter */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Personnes à contacter
              </h2>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </button>
            </div>

            <div className="p-6">
              {contactPersons.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune personne à contacter</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contactPersons.map((person) => (
                    <div key={person.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {person.prenoms} {person.nom}
                          </h3>
                          {person.profession && (
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <Briefcase className="w-4 h-4 mr-1" />
                              {person.profession}
                            </p>
                          )}
                          {person.ville && (
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <MapPin className="w-4 h-4 mr-1" />
                              {person.ville}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                            {person.telephone_domicile && (
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {person.telephone_domicile}
                              </div>
                            )}
                            {person.telephone_portable && (
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {person.telephone_portable}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditPerson(person)}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePerson(person.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Relations */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Relations
              </h2>
            </div>

            <div className="p-6">
              {clientRelations.length === 0 ? (
                <div className="text-center py-8">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune relation définie</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {clientRelations.map((relation) => (
                    <div key={relation.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            {relation.est_principal && (
                              <Star className="w-4 h-4 text-yellow-500 mr-2" />
                            )}
                            <h3 className="font-medium text-gray-900">
                              {relation.personne_a_contacter.prenoms} {relation.personne_a_contacter.nom}
                            </h3>
                          </div>
                          {relation.type_relation_nom && (
                            <p className="text-sm text-gray-600 mt-1">
                              {relation.type_relation_nom}
                            </p>
                          )}
                        </div>
                        {/* <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDeleteRelation(relation.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div> */}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formulaire d'ajout/modification */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingPerson ? 'Modifier la personne à contacter' : 'Ajouter une personne à contacter'}
                </h2>
              </div>

              <form onSubmit={handleSubmitPerson} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="prenoms" className="block text-sm font-medium text-gray-700 mb-1">
                      Prénoms *
                    </label>
                    <input
                      type="text"
                      id="prenoms"
                      name="prenoms"
                      value={formData.prenoms}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="date_naissance" className="block text-sm font-medium text-gray-700 mb-1">
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      id="date_naissance"
                      name="date_naissance"
                      value={formData.date_naissance}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="lieu_naissance" className="block text-sm font-medium text-gray-700 mb-1">
                      Lieu de naissance
                    </label>
                    <input
                      type="text"
                      id="lieu_naissance"
                      name="lieu_naissance"
                      value={formData.lieu_naissance}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="telephone_domicile" className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone domicile
                    </label>
                    <input
                      type="tel"
                      id="telephone_domicile"
                      name="telephone_domicile"
                      value={formData.telephone_domicile}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone_bureau" className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone bureau
                    </label>
                    <input
                      type="tel"
                      id="telephone_bureau"
                      name="telephone_bureau"
                      value={formData.telephone_bureau}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone_portable" className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone portable
                    </label>
                    <input
                      type="tel"
                      id="telephone_portable"
                      name="telephone_portable"
                      value={formData.telephone_portable}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-1">
                      Profession
                    </label>
                    <input
                      type="text"
                      id="profession"
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="ville" className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <input
                      type="text"
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Section Relation */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Relation avec cette personne</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="type_relation" className="block text-sm font-medium text-gray-700 mb-1">
                        Type de relation *
                      </label>
                      <select
                        id="type_relation"
                        name="type_relation"
                        value={formData.type_relation}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={0}>Sélectionnez un type de relation</option>
                        {relations.map((relation) => (
                          <option key={relation.id} value={relation.id}>
                            {relation.libelle}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="est_principal"
                        name="est_principal"
                        checked={formData.est_principal}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="est_principal" className="ml-2 block text-sm text-gray-700">
                        Personne principale à contacter
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingPerson ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
